/**
 * A porta unica do modulo 02 para o backend.
 *
 * ## Por que um erro com `comoResolver`, e nao um `Error` qualquer
 *
 * O manual (regra 10 do checklist) manda a mensagem de erro dizer o que fazer
 * em seguida, nao so o que deu errado. Do lado do servidor isso ja e verdade:
 * toda recusa de rota devolve `{ erro, comoResolver }`, e as recusas do banco
 * (periodo sobreposto, status fora dos seis, codigo repetido) sao traduzidas
 * em `rotas/equipamentos/erros.ts`. Se o cliente jogasse isso fora e mostrasse
 * "Erro ao salvar", o trabalho do outro lado viraria enfeite. Entao o erro
 * carrega as duas metades ate a tela.
 *
 * ## O 401 tem texto proprio
 *
 * A autenticacao EXISTE desde a migracao 0009. Um 401 hoje quer dizer que este
 * navegador nao tem sessao valida — ninguem entrou, ou o prazo de 12 horas
 * passou com a aba aberta. Em vez de deixar isso chegar como "falha ao
 * carregar", que mandaria a pessoa procurar problema no lugar errado, o 401
 * vira uma frase que diz o que fazer: entrar outra vez.
 */

import { buscar } from '@/lib/licenca-das-rotas';
import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';

/**
 * A base e as credenciais vem de `@/lib/api`, num lugar so.
 *
 * Vazio quer dizer `/api`, que o proxy do Vite reescreve em
 * desenvolvimento; num container ela e o endereco completo da API, assado
 * no build por `VITE_API_URL`. Nenhum nome de maquina mora neste arquivo.
 */
const BASE = BASE_DA_API;

export class ErroDaApi extends Error {
  readonly status: number;
  readonly comoResolver: string;
  /** O codigo da recusa, quando o servidor o manda (`ficha_alterada`, `situacao_mudou`). */
  readonly codigo: string | null;

  constructor(status: number, mensagem: string, comoResolver: string, codigo: string | null = null) {
    super(mensagem);
    this.name = 'ErroDaApi';
    this.status = status;
    this.comoResolver = comoResolver;
    this.codigo = codigo;
  }
}

/**
 * A frase de 401, num lugar so.
 *
 * Ela ja afirmou que "a autenticacao ainda nao foi construida (historia
 * F-04)", e isso deixou de ser verdade na migracao 0009: `POST /sessao`
 * existe, confere a senha dentro do banco e abre sessao com prazo. A tela
 * desmentia o proprio botao "Ir para o login", que ficava logo abaixo dela.
 *
 * Um 401 hoje quer dizer UMA coisa: nao ha sessao valida neste navegador —
 * porque ninguem entrou, ou porque as 12 horas do prazo passaram com a aba
 * aberta (o cliente de consulta nao repergunta ao voltar o foco).
 */
export const SEM_SESSAO =
  'Sua sessão não está aberta, ou expirou. Entre outra vez para ver a frota.';

interface CorpoDeErro {
  readonly erro?: string;
  readonly comoResolver?: string;
  readonly codigo?: unknown;
}

async function lerCorpo(resposta: Response): Promise<unknown> {
  const texto = await resposta.text();
  if (!texto) return null;
  try {
    return JSON.parse(texto) as unknown;
  } catch {
    return texto;
  }
}

/**
 * `credentials` nao e detalhe: o token de sessao e um cookie opaco e
 * `HttpOnly`, e sem isto ele nao viaja. Vale `'include'` (ver `@/lib/api`)
 * porque a API passou a morar noutra maquina, e `'same-origin'` o omitiria
 * em silencio — toda rota responderia 401 sem nada parecer errado no
 * servidor.
 */
export async function pedir<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  let resposta: Response;
  try {
    /* A leitura sem a licenca da rota nem sai: ver `@/lib/licenca-das-rotas`. */
    resposta = await buscar(`${BASE}${caminho}`, caminho, {
      credentials: CREDENCIAIS_DA_API,
      headers: opcoes.body ? { 'content-type': 'application/json' } : {},
      ...opcoes,
    });
  } catch {
    throw new ErroDaApi(
      0,
      'O sistema não respondeu',
      'Confira se o backend está no ar e tente carregar de novo; se continuar, avise o suporte com o horário.',
    );
  }

  const corpo = await lerCorpo(resposta);

  if (!resposta.ok) {
    if (resposta.status === 401) {
      throw new ErroDaApi(401, 'Sua sessão não está aberta', SEM_SESSAO);
    }
    const detalhe = (typeof corpo === 'object' && corpo !== null ? corpo : {}) as CorpoDeErro;
    throw new ErroDaApi(
      resposta.status,
      detalhe.erro ?? 'Não foi possível concluir',
      detalhe.comoResolver ?? 'Confira os dados informados e tente de novo.',
      typeof detalhe.codigo === 'string' ? detalhe.codigo : null,
    );
  }

  return corpo as T;
}

/** Instante do banco, que chega em ISO 8601, ou nulo. */
export function paraInstante(valor: unknown): Date | null {
  if (typeof valor !== 'string' || !valor) return null;
  const quando = new Date(valor);
  return Number.isNaN(quando.getTime()) ? null : quando;
}

/**
 * `numeric` do Postgres chega como TEXTO no driver `pg`, e de proposito: em
 * `double` um total de litros perde centavo. Aqui ele vira numero para a tela
 * formatar — e a conversao mora num lugar so, para nenhuma tela inventar a
 * dela.
 */
export function paraNumero(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(n) ? n : null;
}

export function paraTexto(valor: unknown): string | null {
  return typeof valor === 'string' && valor.length > 0 ? valor : null;
}
